import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Settings, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Save,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';
import RecordPaymentModal from './RecordPaymentModal';
import RecordBillModal from './RecordBillModal';
import ClientTimelineModal from './ClientTimelineModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function PaymentsManager({ activeSubTab = 'transactions' }) {
  const [activeTab, setActiveTab] = useState(activeSubTab);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLedger, setExpandedLedger] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [bills, setBills] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [txSearchTerm, setTxSearchTerm] = useState('');
  const [ledgerSearchTerm, setLedgerSearchTerm] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterSchool, setFilterSchool] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [timelineClientEmail, setTimelineClientEmail] = useState(null);

  useEffect(() => {
    if (activeSubTab) {
      setActiveTab(activeSubTab);
    }
  }, [activeSubTab]);
  
  // Settings State
  const [razorpayKey, setRazorpayKey] = useState('');
  const [razorpaySecret, setRazorpaySecret] = useState('');
  const [savingGateway, setSavingGateway] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    const fetchPaymentsAndSettings = async () => {
      setLoading(true);
      try {
        const [paymentsRes, gatewayRes, billsRes] = await Promise.all([
          axiosClient.get('/admin/payments'),
          axiosClient.get('/admin/settings/gateway'),
          axiosClient.get('/admin/bills')
        ]);
        setPayments(paymentsRes.data.payments || []);
        setBills(billsRes.data.bills || []);
        
        if (gatewayRes.data) {
          setRazorpayKey(gatewayRes.data.razorpayKey || '');
          setRazorpaySecret(gatewayRes.data.razorpaySecret || '');
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentsAndSettings();
  }, [activeTab]);

  const refreshPayments = async () => {
    try {
      const [paymentsRes, billsRes] = await Promise.all([
        axiosClient.get('/admin/payments'),
        axiosClient.get('/admin/bills')
      ]);
      setPayments(paymentsRes.data.payments || []);
      setBills(billsRes.data.bills || []);
    } catch (err) {
      console.error('Failed to refresh data:', err);
    }
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200"><CheckCircle size={12} /> Completed</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 border border-red-200"><XCircle size={12} /> Failed</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 border border-amber-200"><Clock size={12} /> Pending</span>;
      default:
        return null;
    }
  };

  const uniqueClients = [...new Map([
    ...payments.map(p => [p.email, { name: p.name, email: p.email, school_name: p.school_name, plan_name: p.plan_name }]),
    ...bills.map(b => [b.client_email, { name: b.client_name, email: b.client_email, school_name: b.school_name, plan_name: 'Custom' }])
  ]).values()];

  const ledgers = uniqueClients.map(client => {
    const clientPayments = payments.filter(p => p.email === client.email).map(p => ({
      ...p,
      type: 'Credit',
      date: new Date(p.created_at)
    }));
    const clientBills = bills.filter(b => b.client_email === client.email).map(b => ({
      ...b,
      type: 'Debit',
      date: new Date(b.created_at)
    }));

    const entries = [...clientPayments, ...clientBills].sort((a, b) => {
      const diff = a.date - b.date;
      if (diff !== 0) return diff;
      // If timestamps are the same, Debit (Bill) comes before Credit (Payment)
      if (a.type === 'Debit' && b.type === 'Credit') return -1;
      if (a.type === 'Credit' && b.type === 'Debit') return 1;
      return 0;
    });
    
    let runningBalance = 0;
    const ledgerEntries = entries.map(entry => {
      if (entry.type === 'Debit') {
        runningBalance += parseFloat(entry.amount);
      } else {
        runningBalance -= parseFloat(entry.amount_paid);
      }
      return { ...entry, balance: runningBalance };
    });

    const total_paid = clientPayments.reduce((sum, p) => sum + parseFloat(p.amount_paid), 0);
    const total_billed = clientBills.reduce((sum, b) => sum + parseFloat(b.amount), 0);

    return {
      ...client,
      total_paid,
      total_billed,
      current_balance: runningBalance,
      transactions: ledgerEntries
    };
  });

  const filteredLedgers = ledgers.filter(ledger => {
    if (!ledgerSearchTerm) return true;
    const term = ledgerSearchTerm.toLowerCase();
    return (ledger.name && ledger.name.toLowerCase().includes(term)) ||
           (ledger.email && ledger.email.toLowerCase().includes(term)) ||
           (ledger.school_name && ledger.school_name.toLowerCase().includes(term));
  });

  const downloadPDF = async (ledger) => {
    try {
      const doc = new jsPDF();
      const img = new Image();
      img.src = '/logo-white.jpg';
      
      let imageLoaded = false;
      await new Promise((resolve) => {
        img.onload = () => { imageLoaded = true; resolve(); };
        img.onerror = resolve; 
      });
      
      if (imageLoaded) {
        try {
          doc.addImage(img, 'JPEG', 14, 10, 40, 10);
        } catch(e) { console.error('Image add failed', e); }
      }

      doc.setFontSize(16);
      doc.text(`Ledger Statement`, 14, 28);
      doc.setFontSize(10);
      doc.text(`Client: ${ledger.name} (${ledger.school_name})`, 14, 36);
      doc.text(`Email: ${ledger.email}`, 14, 42);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 48);

      // Group by month
      const grouped = ledger.transactions.reduce((acc, tx) => {
        const month = new Date(tx.date).toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!acc[month]) acc[month] = [];
        acc[month].push(tx);
        return acc;
      }, {});

      let startY = 55;
      for (const [month, txs] of Object.entries(grouped)) {
        doc.setFontSize(12);
        doc.text(`Month: ${month}`, 14, startY);
        startY += 5;
        
        const tableData = txs.map(tx => [
          new Date(tx.date).toLocaleDateString(),
          tx.type === 'Debit' ? tx.description : `Payment (${tx.payment_mode})`,
          tx.type === 'Debit' ? `Rs. ${tx.amount}` : '-',
          tx.type === 'Credit' ? `Rs. ${tx.amount_paid}` : '-',
          `Rs. ${Math.abs(tx.balance)} ${tx.balance > 0 ? 'Dr' : 'Cr'}`
        ]);

        autoTable(doc, {
          startY: startY,
          head: [['Date', 'Description', 'Debit', 'Credit', 'Balance']],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42] },
          margin: { top: 10 }
        });
        
        startY = (doc.lastAutoTable?.finalY || startY + 20) + 15;
      }

      doc.save(`${ledger.school_name}_Ledger.pdf`);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      alert("Failed to generate PDF. Check console for details.");
    }
  };

  const uniqueSchools = [...new Set(payments.map(p => p.school_name).filter(Boolean))].sort();

  const filteredPayments = payments.filter(tx => {
    // text search
    if (txSearchTerm) {
      const term = txSearchTerm.toLowerCase();
      const match = (tx.id && tx.id.toString().includes(term)) || 
                    (tx.name && tx.name.toLowerCase().includes(term)) ||
                    (tx.school_name && tx.school_name.toLowerCase().includes(term));
      if (!match) return false;
    }
    
    // filter by school
    if (filterSchool && tx.school_name !== filterSchool) {
      return false;
    }

    // filter by date
    if (filterDateStart || filterDateEnd) {
      const txDate = new Date(tx.created_at);
      if (filterDateStart && txDate < new Date(filterDateStart)) return false;
      if (filterDateEnd) {
        const endD = new Date(filterDateEnd);
        endD.setHours(23, 59, 59, 999);
        if (txDate > endD) return false;
      }
    }

    return true;
  });

  return (
    <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="p-6">
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Recent Payments</h3>
              <div className="flex gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input 
                    type="text" 
                    value={txSearchTerm}
                    onChange={(e) => setTxSearchTerm(e.target.value)}
                    placeholder="Search by ID, Name or School..." 
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
                <button 
                  onClick={() => setShowRecordModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  <CreditCard size={16} />
                  Record Payment
                </button>
                <div className="relative">
                  <button 
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className={`relative flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${showFilterDropdown || filterSchool || filterDateStart || filterDateEnd ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 border-primary-200 dark:border-primary-800' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-[#0b1220]'}`}
                  >
                    <Filter size={16} />
                    Filter {(filterSchool || filterDateStart || filterDateEnd) && <span className="w-2 h-2 rounded-full bg-primary-600 absolute top-2 right-2"></span>}
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-[#1e2336] rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-4 z-10">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">School Name</label>
                          <select 
                            value={filterSchool}
                            onChange={e => setFilterSchool(e.target.value)}
                            className="w-full text-sm py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-primary-500 dark:bg-[#0b1220] text-slate-700 dark:text-slate-300"
                          >
                            <option value="">All Schools</option>
                            {uniqueSchools.map((school, i) => (
                              <option key={i} value={school}>{school}</option>
                            ))}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Start Date</label>
                            <input 
                              type="date" 
                              value={filterDateStart}
                              onChange={e => setFilterDateStart(e.target.value)}
                              className="w-full text-sm py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-primary-500 dark:bg-[#0b1220] text-slate-700 dark:text-slate-300 [&::-webkit-calendar-picker-indicator]:dark:invert"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">End Date</label>
                            <input 
                              type="date" 
                              value={filterDateEnd}
                              onChange={e => setFilterDateEnd(e.target.value)}
                              className="w-full text-sm py-2 px-3 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-primary-500 dark:bg-[#0b1220] text-slate-700 dark:text-slate-300 [&::-webkit-calendar-picker-indicator]:dark:invert"
                            />
                          </div>
                        </div>
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                          <button 
                            onClick={() => {
                              setFilterSchool('');
                              setFilterDateStart('');
                              setFilterDateEnd('');
                            }}
                            className="text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-xl">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 dark:bg-[#0b1220] border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium">
                  <tr>
                    <th className="px-6 py-4">ID</th>
                    <th className="px-6 py-4">Transaction ID</th>
                    <th className="px-6 py-4">Customer</th>
                    <th className="px-6 py-4">School</th>
                    <th className="px-6 py-4">Plan</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Date & Time</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading payments...</td>
                    </tr>
                  ) : filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">No payments found.</td>
                    </tr>
                  ) : filteredPayments.map((tx) => (
                    <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-slate-700 dark:text-slate-200">#{tx.id}</td>
                      <td className="px-6 py-4 font-mono text-xs text-slate-500 dark:text-slate-400">
                        {tx.razorpay_payment_id || 'OFFLINE'}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                        {tx.name}
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-normal">{tx.email}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{tx.school_name}</td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                        {tx.plan_name}
                        <div className="text-xs text-slate-400">{tx.students_count} students</div>
                      </td>
                      <td className="px-6 py-4 font-semibold">
                        ₹{(tx.amount_paid).toLocaleString()}
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider mt-0.5">{tx.payment_mode === 'full' ? 'Full' : 'Partial'}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-500 dark:text-slate-400 text-sm">{new Date(tx.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4">{renderStatusBadge(tx.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-between items-center text-sm text-slate-500 dark:text-slate-400">
              <p>Showing {filteredPayments.length} transactions</p>
            </div>
          </div>
        )}

        {activeTab === 'ledgers' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Client Ledgers</h3>
              <div className="relative flex-1 sm:w-64 max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="text" 
                  value={ledgerSearchTerm}
                  onChange={(e) => setLedgerSearchTerm(e.target.value)}
                  placeholder="Search clients..." 
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="p-8 text-center text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50">Loading ledgers...</div>
              ) : filteredLedgers.length === 0 ? (
                <div className="p-8 text-center text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50/50">No clients found.</div>
              ) : (
                filteredLedgers.map((ledger) => (
                  <div key={ledger.email} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-[#1e2336] shadow-sm hover:shadow transition-shadow">
                    <div 
                      className="flex items-center justify-between p-5 cursor-pointer select-none"
                      onClick={() => {
                        setExpandedLedger(expandedLedger === ledger.email ? null : ledger.email);
                        setLedgerPage(1);
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 dark:text-primary-400 flex items-center justify-center font-bold text-lg">
                          {ledger.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 dark:text-white">{ledger.school_name}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{ledger.name} ({ledger.email})</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider mb-0.5">Balance</p>
                          <p className={`font-bold ${ledger.current_balance > 0 ? 'text-red-500' : 'text-emerald-500'}`}>₹{Math.abs(ledger.current_balance).toLocaleString()} {ledger.current_balance > 0 ? '(Due)' : ''}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-slate-50 dark:bg-[#0b1220] flex items-center justify-center text-slate-400">
                          {expandedLedger === ledger.email ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </div>
                    </div>
                    
                    {expandedLedger === ledger.email && (
                      <div className="bg-slate-50 dark:bg-[#0b1220] border-t border-slate-100 dark:border-slate-800 p-5">
                        <div className="flex justify-between items-center mb-4 px-1">
                          <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Ledger Details</h5>
                          <button 
                            onClick={() => downloadPDF(ledger)}
                            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors"
                          >
                            Download PDF
                          </button>
                        </div>
                        <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-[#1e2336]">
                          <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-slate-50 dark:bg-[#0b1220] border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400">
                              <tr>
                                <th className="px-4 py-3 font-medium">Date</th>
                                <th className="px-4 py-3 font-medium">Description</th>
                                <th className="px-4 py-3 font-medium text-right">Debit (₹)</th>
                                <th className="px-4 py-3 font-medium text-right">Credit (₹)</th>
                                <th className="px-4 py-3 font-medium text-right">Balance (₹)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                              {ledger.transactions.length === 0 ? (
                                <tr>
                                  <td colSpan="5" className="px-4 py-6 text-center text-slate-500">No entries found.</td>
                                </tr>
                              ) : ledger.transactions.slice((ledgerPage - 1) * 5, ledgerPage * 5).map((tx, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{new Date(tx.date).toLocaleDateString()}</td>
                                  <td className="px-4 py-3 text-slate-900 dark:text-white font-medium">
                                    {tx.type === 'Debit' ? tx.description : `Payment (${tx.payment_mode})`}
                                    {tx.type === 'Credit' && tx.plan_name && <span className="ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] text-slate-500">{tx.plan_name}</span>}
                                  </td>
                                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">{tx.type === 'Debit' ? (tx.amount ?? 0).toLocaleString() : '-'}</td>
                                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">{tx.type === 'Credit' ? (tx.amount_paid ?? 0).toLocaleString() : '-'}</td>
                                  <td className={`px-4 py-3 text-right font-semibold ${tx.balance > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                                    {Math.abs(tx.balance).toLocaleString()} {tx.balance > 0 ? 'Dr' : 'Cr'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {Math.ceil(ledger.transactions.length / 5) > 1 && (
                          <div className="mt-4 flex justify-center gap-2">
                            {Array.from({ length: Math.ceil(ledger.transactions.length / 5) }).map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setLedgerPage(i + 1)}
                                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${ledgerPage === i + 1 ? 'bg-primary-600 text-white' : 'bg-white dark:bg-[#1e2336] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={() => setLedgerPage(Math.min(Math.ceil(ledger.transactions.length / 5), ledgerPage + 1))}
                              className="px-3 py-1.5 bg-white dark:bg-[#1e2336] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                              Next Page
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Client Orders & Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {uniqueClients.map((client, idx) => (
                <div key={idx} className="bg-white dark:bg-[#1e2336] border border-slate-200 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">{client.school_name}</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{client.name} ({client.email})</p>
                  </div>
                  <div className="flex flex-col gap-3 mt-4">
                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-[#0b1220] rounded-lg border border-slate-100 dark:border-slate-800">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Active Plan</span>
                      <span className="px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 rounded-full text-xs font-bold">{client.plan_name || 'N/A'}</span>
                    </div>
                    <button 
                      onClick={() => setTimelineClientEmail(client.email)}
                      className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      <Clock size={16} />
                      View 360 Timeline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bills' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Bills</h3>
              <button 
                onClick={() => setShowBillModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Create Bill
              </button>
            </div>
            
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-[#1e2336]">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 dark:bg-[#0b1220] border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium">
                  <tr>
                    <th className="px-6 py-4">ID</th>
                    <th className="px-6 py-4">Client</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {bills.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-slate-500">No bills found.</td>
                    </tr>
                  ) : bills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-slate-700 dark:text-slate-200">#{bill.id}</td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900 dark:text-white">{bill.client_name}</div>
                        <div className="text-xs text-slate-500">{bill.school_name}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{bill.description}</td>
                      <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">₹{bill.amount?.toLocaleString() || '0'}</td>
                      <td className="px-6 py-4 text-slate-500 text-sm">{new Date(bill.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Razorpay Configuration</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-8">Enter your Razorpay API credentials to enable payment processing across the application.</p>
            
            <div className="space-y-6 bg-white dark:bg-[#1e2336] p-6 rounded-2xl border border-slate-200 dark:border-slate-700">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Razorpay Key ID</label>
                  <input 
                    type="text" 
                    placeholder="rzp_test_..."
                    value={razorpayKey}
                    onChange={(e) => setRazorpayKey(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                  />
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">The Key ID generated from your Razorpay dashboard.</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Razorpay Key Secret</label>
                  <div className="relative">
                    <input 
                      type={showSecret ? "text" : "password"} 
                      placeholder="••••••••••••••••"
                      value={razorpaySecret}
                      onChange={(e) => setRazorpaySecret(e.target.value)}
                      className="w-full px-4 py-2 pr-10 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none font-mono"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    >
                      {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">The secret associated with your Key ID. Keep this secure.</p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                <button 
                  className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-bold hover:bg-primary-700 transition-colors disabled:opacity-50"
                  onClick={async () => {
                    setSavingGateway(true);
                    try {
                      await axiosClient.post('/admin/settings/gateway', { razorpayKey, razorpaySecret });
                      toast.success('Gateway settings saved! Please restart the backend server for changes to fully apply to .env.');
                    } catch (err) {
                      toast.error('Failed to save settings.');
                    } finally {
                      setSavingGateway(false);
                    }
                  }}
                  disabled={savingGateway}
                >
                  <Save size={16} />
                  {savingGateway ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <RecordPaymentModal 
        isOpen={showRecordModal} 
        onClose={() => setShowRecordModal(false)} 
        onSuccess={refreshPayments} 
        payments={payments}
      />
      {showBillModal && (
        <RecordBillModal 
          onClose={() => setShowBillModal(false)}
          onSuccess={() => {
            setShowBillModal(false);
            refreshPayments();
          }}
          clients={uniqueClients}
        />
      )}

      {timelineClientEmail && (
        <ClientTimelineModal 
          clientEmail={timelineClientEmail} 
          onClose={() => setTimelineClientEmail(null)} 
        />
      )}
    </div>
  );
}
